// src/pages/superadmin/AuditLogs.jsx
//Zero Firebase imports — all data via auditLogService → repo
import React, { useState, useEffect, useMemo, useCallback } from "react";

// ── Service ───────────────────────────────────────────────────────────────────
import { auditLogService } from "../../services/auditLogService";

// ── Auth ──────────────────────────────────────────────────────────────────────
import { useAuth } from "../../contexts/AuthContext";

// ── Shared UI components ──────────────────────────────────────────────────────
import DataTable              from "../../components/common/DataTable";
import ToastNotification      from "../../components/common/ToastNotification";
import DeleteConfirmationModal from "../../components/Modals/DeleteConfirmationModal";
import PermissionDenied        from "../../components/common/PermissionDenied";

import "./AuditLogs.css";

/* ═══════════════════════════════════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════════════════════════════════ */

const ACTION_TYPES = ["all", "create", "update", "delete", "login", "logout"];

/* ═══════════════════════════════════════════════════════════════════════════
   SKELETON
═══════════════════════════════════════════════════════════════════════════ */

const AuditLogsSkeleton = () => (
  <div className="audit-loading-state">
    <div className="audit-spinner" />
    <p>Loading audit logs…</p>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════════ */

const AuditLogs = () => {
  const { currentUser } = useAuth();

  /* ── Raw data from subscription ─────────────────────────────────────────── */
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading,   setLoading]   = useState(true);

  /* ── UI state ───────────────────────────────────────────────────────────── */
  const [notification,    setNotification]    = useState(null);
  const [searchTerm,      setSearchTerm]      = useState("");
  const [selectedAction,  setSelectedAction]  = useState("all");
  const [selectedUser,    setSelectedUser]    = useState("all");
  const [selectedLogIds,  setSelectedLogIds]  = useState([]);
  const [showModal,       setShowModal]       = useState(false);
  const [deleting,        setDeleting]        = useState(false);
  const [permissionError, setPermissionError] = useState(null);

  /* ── Notification helper ─────────────────────────────────────────────────── */
  const showNotification = useCallback((message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  }, []);

  /* ─────────────────────────────────────────────────────────────────────────
     SUBSCRIPTION — via auditLogService → repo
  ───────────────────────────────────────────────────────────────────────── */

  useEffect(() => {
    // Guard — only superadmin may access
    if (!currentUser) return;

    if (currentUser.role !== "superadmin") {
      setPermissionError("Only Super Admins can access Audit Logs.");
      setLoading(false);
      return;
    }

    setLoading(true);

    let cleanup;

    try {
      const result = auditLogService.subscribeToAuditLogs(
        currentUser,
        (normalisedLogs) => {
          setAuditLogs(normalisedLogs);

          if (normalisedLogs.length === 0) {
            showNotification("No audit logs found.", "info");
          }

          setLoading(false);
        },
      );

      // Handle both sync (fn) and async (Promise<fn>) paths from repo
      if (result instanceof Promise) {
        result
          .then((fn) => { cleanup = fn; })
          .catch((err) => {
            showNotification(`Subscription error: ${err.message}`, "error");
            setLoading(false);
          });
      } else {
        cleanup = result;
      }
    } catch (err) {
      // _assertSuperAdmin throws synchronously if role is wrong
      setPermissionError(err.message);
      setLoading(false);
    }

    return () => {
      if (typeof cleanup === "function") cleanup();
    };
  }, [currentUser, showNotification]);

  /* ─────────────────────────────────────────────────────────────────────────
     DERIVED STATE — filters applied in-memory via service helper
  ───────────────────────────────────────────────────────────────────────── */

  const filteredLogs = useMemo(
    () =>
      auditLogService.applyFilters(auditLogs, {
        searchTerm,
        selectedAction,
        selectedUser,
      }),
    [auditLogs, searchTerm, selectedAction, selectedUser],
  );

  const uniqueUsers = useMemo(
    () => auditLogService.extractUniqueUsers(auditLogs),
    [auditLogs],
  );

  /* ─────────────────────────────────────────────────────────────────────────
     DELETE HANDLER — via auditLogService → repo
  ───────────────────────────────────────────────────────────────────────── */

  const handleDeleteConfirmed = async () => {
    setDeleting(true);
    try {
      const { deleted } = await auditLogService.bulkDeleteLogs(
        selectedLogIds,
        currentUser,
      );
      showNotification(`Deleted ${deleted} selected log${deleted !== 1 ? "s" : ""}.`);
      setSelectedLogIds([]);
    } catch (err) {
      console.error("Error deleting logs:", err.message);
      showNotification(`Error deleting logs: ${err.message}`, "error");
    } finally {
      setDeleting(false);
      setShowModal(false);
    }
  };

  /* ─────────────────────────────────────────────────────────────────────────
     SELECTION HELPERS
  ───────────────────────────────────────────────────────────────────────── */

  const handleToggleSelect = (id, checked) => {
    setSelectedLogIds((prev) =>
      checked ? [...prev, id] : prev.filter((i) => i !== id),
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedLogIds(checked ? filteredLogs.map((l) => l.id) : []);
  };

  const allSelected =
    filteredLogs.length > 0 &&
    selectedLogIds.length === filteredLogs.length;

  const someSelected =
    selectedLogIds.length > 0 &&
    selectedLogIds.length < filteredLogs.length;

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedAction("all");
    setSelectedUser("all");
  };

  /* ─────────────────────────────────────────────────────────────────────────
     TABLE COLUMNS
  ───────────────────────────────────────────────────────────────────────── */

  const columns = [
    {
      header: (
        <input
          type="checkbox"
          checked={allSelected}
          ref={(el) => { if (el) el.indeterminate = someSelected; }}
          onChange={(e) => handleSelectAll(e.target.checked)}
          title="Select all on this page"
        />
      ),
      accessor: "checkbox",
      render: (row) => (
        <input
          type="checkbox"
          checked={selectedLogIds.includes(row.id)}
          onChange={(e) => handleToggleSelect(row.id, e.target.checked)}
        />
      ),
    },
    {
      header:   "Timestamp",
      accessor: "timestamp",
      render:   (row) => auditLogService.formatDate(row.timestamp),
    },
    {
      header:   "User",
      accessor: "userEmail",
      render:   (row) => row.userEmail || "System",
    },
    {
      header:   "Action",
      accessor: "actionType",
      render:   (row) => (
        <span
          className={`audit-action-badge ${(row.actionType || "unknown").toLowerCase()}`}
        >
          {row.actionType || "unknown"}
        </span>
      ),
    },
    {
      header:   "Entity",
      accessor: "entityType",
      render:   (row) => row.entityType || "—",
    },
    {
      header:   "Description",
      accessor: "description",
      render:   (row) => row.description || "—",
    },
  ];

  /* ─────────────────────────────────────────────────────────────────────────
     GUARDS
  ───────────────────────────────────────────────────────────────────────── */

  if (permissionError) {
    return (
      <div className="audit-logs-container">
        <PermissionDenied message={permissionError} />
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────────────────────────────────── */

  return (
    <div className="audit-logs-container">

      {/* ── TOAST ───────────────────────────────────────────────────────── */}
      {notification && (
        <ToastNotification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* ── DELETE CONFIRMATION MODAL ────────────────────────────────────── */}
      {showModal && (
        <DeleteConfirmationModal
          itemName={`${selectedLogIds.length} selected log${selectedLogIds.length !== 1 ? "s" : ""}`}
          onClose={() => setShowModal(false)}
          onConfirm={handleDeleteConfirmed}
          loading={deleting}
        />
      )}

      

      {/* ── FILTERS ─────────────────────────────────────────────────────── */}
      <div className="audit-filters-wrapper">

        {/* Action type */}
        <div className="audit-filter-group">
          <label htmlFor="action-filter">Action Type:</label>
          <select
            id="action-filter"
            value={selectedAction}
            onChange={(e) => setSelectedAction(e.target.value)}
          >
            {ACTION_TYPES.map((action) => (
              <option key={action} value={action}>
                {action === "all"
                  ? "All Actions"
                  : action.charAt(0).toUpperCase() + action.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* User */}
        <div className="audit-filter-group">
          <label htmlFor="user-filter">User:</label>
          <select
            id="user-filter"
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            disabled={loading || uniqueUsers.length === 0}
          >
            <option value="all">All Users</option>
            {uniqueUsers.map((user) => (
              <option key={user} value={user}>
                {user}
              </option>
            ))}
          </select>
        </div>

        {/* Search */}
        <div className="audit-search-box">
          <i className="bi bi-search" />
          <input
            type="text"
            placeholder="Search logs…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className="audit-clear-search"
              onClick={() => setSearchTerm("")}
              title="Clear search"
            >
              <i className="bi bi-x" />
            </button>
          )}
        </div>
      </div>

      {/* ── BULK ACTION TOOLBAR ──────────────────────────────────────────── */}
      {selectedLogIds.length > 0 && (
        <div className="audit-selected-toolbar">
          <span>
            {selectedLogIds.length} log{selectedLogIds.length !== 1 ? "s" : ""} selected
          </span>
          <button
            onClick={() => setShowModal(true)}
            className="audit-delete-btn"
          >
            <i className="bi bi-trash" /> Delete Selected
          </button>
          <button
            onClick={() => setSelectedLogIds([])}
            className="audit-clear-selection-btn"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* ── LOADING ─────────────────────────────────────────────────────── */}
      {loading ? (
        <AuditLogsSkeleton />
      ) : (
        <>
          {/* Active filter tags */}
          {(selectedAction !== "all" || selectedUser !== "all") && (
            <div className="audit-active-filters">
              {selectedAction !== "all" && (
                <span className="audit-filter-tag">
                  Action: {selectedAction}
                  <button onClick={() => setSelectedAction("all")}>
                    <i className="bi bi-x" />
                  </button>
                </span>
              )}
              {selectedUser !== "all" && (
                <span className="audit-filter-tag">
                  User: {selectedUser}
                  <button onClick={() => setSelectedUser("all")}>
                    <i className="bi bi-x" />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Empty state */}
          {filteredLogs.length === 0 ? (
            <div className="audit-empty-state">
              <i className="bi bi-journal-text" />
              <h3>No Audit Logs Found</h3>
              <p>No logs match your current filters.</p>
              <button className="audit-reset-btn" onClick={resetFilters}>
                Reset Filters
              </button>
            </div>
          ) : (
            <>
              {/* Result count */}
              <p className="audit-result-count">
                Showing <strong>{filteredLogs.length}</strong> of{" "}
                <strong>{auditLogs.length}</strong> log
                {auditLogs.length !== 1 ? "s" : ""}
              </p>

              {/* Data table */}
              <DataTable
                columns={columns}
                data={filteredLogs}
                emptyMessage="No audit logs found"
                pagination
                itemsPerPage={10}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AuditLogs;